import usePeriods from "backend/hooks/model/usePeriods";
import * as conf from "qpr-base/configuration";
import React from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CircleCheckbox from "../CircleCheckBox/CircleCheckbox";
import usePeriodSelectorStyles from "./Themes";

type option = { label: string; value: string };

type Props = {
  elementId?: string;
  periodLevel?: string;
  from?: string;
  to?: string;
  onChange?: (startPeriod: string, endPeriod?: string) => void;
};

const PeriodSelector = (props: Props) => {
  const { t } = useTranslation();
  const classes = usePeriodSelectorStyles();

  const [startPeriodName, setStart] = React.useState<string>(props.from || "");
  const [endPeriodName, setEnd] = React.useState<string>(props.to || "");
  const [periods, setPeriods] = React.useState<option[]>([]);
  const { isLoading: isPeriodsLoading, data: periodData } = usePeriods(
    props.periodLevel
  );

  const getPeriodDate = (periodName: string, startDate: boolean): Number => {
    if (!periodData) return 0;
    const period = periodData.find((p) => p.name === periodName);
    if (!period) return 0;
    if (startDate) return new Date(period.startdate).getTime();
    return new Date(period.enddate).getTime();
  };

  const getEndPeriods = (startPeriodName: string) => {
    const startdate = getPeriodDate(startPeriodName, true);
    return (
      periods?.filter((v) => getPeriodDate(v.value, true) > startdate) || []
    );
  };

  const isChecked = React.useMemo(() => !!endPeriodName, [endPeriodName]);

  React.useEffect(() => {
    if (!isPeriodsLoading && periodData && props.periodLevel) {
      const selectablePeriods = conf.getMyStoriesSelectablePeriodsSettings();
      const startDate = new Date(parseInt(selectablePeriods.firstYear), 0, 1);
      const lastDate = new Date(parseInt(selectablePeriods.lastYear), 11, 31);
      const periodsInRange = periodData.filter((p) => {
        const periodStartDate = new Date(p.startdate);
        return periodStartDate >= startDate && periodStartDate <= lastDate;
      });
      setPeriods(
        periodsInRange.map((period) => {
          return { label: period.name, value: period.name };
        }) || []
      );
    } else setPeriods([]);
  }, [periodData]);

  React.useEffect(() => {
    if (props.onChange && startPeriodName) {
      props.onChange(startPeriodName, endPeriodName);
    }
  }, [startPeriodName, endPeriodName]);

  if (isPeriodsLoading) {
    return null;
  }
  return (
    <div className={classes.container}>
      <div className={classes.periodSelect1}>
        <Select
          placeholder={`${t("myStories.Select")}...`}
          options={periods}
          value={periods.filter((v) => v.value === startPeriodName)}
          styles={{ menuList: (styles) => ({ ...styles, maxHeight: "120px" }) }}
          onChange={(value) => {
            if (
              getPeriodDate(value?.value || "", true) >
              getPeriodDate(endPeriodName, true)
            )
              setEnd("");
            setStart(value?.value || "");
          }}
        />
      </div>
      <div className={classes.periodSelect2}>
        <CircleCheckbox
          setEnd={setEnd}
          isChecked={isChecked}
          label={t("myStories.To")}
        />
        <Select
          placeholder={`${t("myStories.Select")}...`}
          options={getEndPeriods(startPeriodName)}
          value={periods.filter((v) => v.value === endPeriodName)}
          styles={{ menuList: (styles) => ({ ...styles, maxHeight: "120px" }) }}
          onChange={(value) => {
            if (
              getPeriodDate(value?.value || "", true) <
              getPeriodDate(startPeriodName, true)
            )
              setStart("");
            setEnd(value?.value || "");
          }}
        />
      </div>
    </div>
  );
};

export default PeriodSelector;
